<template>
  <div class="sv-ledger-page">
    <Card>
      <div class="stat-row">
        <div class="stat-card">
          <div class="stat-label">用户余额总值</div>
          <div class="stat-value">¥{{ yuan(summary.pool_balance_fen) }}</div>
          <div class="stat-sub">有余额用户 {{ summary.user_with_balance || 0 }} 人</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">累计充值入账</div>
          <div class="stat-value up">¥{{ yuan(summary.recharge_fen) }}</div>
          <div class="stat-sub">
            订单实付 ¥{{ yuan(summary.recharge_order_pay_fen) }} · 赠送 ¥{{ yuan(summary.recharge_order_gift_fen) }}
            （{{ summary.recharge_order_count || 0 }} 笔）
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">累计消费扣款</div>
          <div class="stat-value down">¥{{ yuan(summary.consume_fen) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">退款回退</div>
          <div class="stat-value up">¥{{ yuan(summary.refund_fen) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">调账</div>
          <div class="stat-value">
            +¥{{ yuan(summary.adjust_in_fen) }} / -¥{{ yuan(summary.adjust_out_fen) }}
          </div>
        </div>
      </div>

      <Form :label-width="80" inline class="m-t-16">
        <FormItem label="关键词">
          <Input
            v-model="query.keyword"
            placeholder="手机号 / 昵称 / 用户ID"
            clearable
            style="width: 200px"
          />
        </FormItem>
        <FormItem label="类型">
          <Select v-model="query.biz_type" clearable placeholder="全部" style="width: 120px">
            <Option value="">全部</Option>
            <Option value="recharge">充值</Option>
            <Option value="consume">消费</Option>
            <Option value="refund">退款</Option>
            <Option value="adjust">调账</Option>
          </Select>
        </FormItem>
        <FormItem label="日期">
          <DatePicker
            v-model="query.dateRange"
            type="daterange"
            placement="bottom-start"
            placeholder="选择日期范围"
            style="width: 220px"
          />
        </FormItem>
        <FormItem :label-width="12">
          <Button type="primary" :loading="loading" @click="search">查询</Button>
          <Button class="m-l-10" @click="resetQuery">重置</Button>
          <Button class="m-l-10" @click="load">刷新</Button>
        </FormItem>
      </Form>

      <Tabs v-model="tab" class="m-t-8" @on-click="onTab">
        <TabPane label="余额流水" name="ledger" />
        <TabPane label="充值订单" name="orders" />
      </Tabs>

      <Table
        v-show="tab === 'ledger'"
        class="m-t-8"
        :loading="loading"
        :columns="ledgerColumns"
        :data="list"
        border
        stripe
      />
      <Table
        v-show="tab === 'orders'"
        class="m-t-8"
        :loading="orderLoading"
        :columns="orderColumns"
        :data="orders"
        border
        stripe
      />

      <Page
        v-show="(tab === 'ledger' ? total : orderTotal) > 0"
        class="pager"
        :total="tab === 'ledger' ? total : orderTotal"
        :current="tab === 'ledger' ? query.page : orderQuery.page"
        :page-size="tab === 'ledger' ? query.pageSize : orderQuery.pageSize"
        :page-size-opts="[10, 20, 50]"
        show-sizer
        show-elevator
        show-total
        @on-change="onPageChange"
        @on-page-size-change="onPageSizeChange"
      />
    </Card>
  </div>
</template>

<script>
import { storedValueLedgerReport, storedValueRechargeOrders } from '@/api/storedValue'

function pad(n) {
  return n < 10 ? `0${n}` : String(n)
}

function ymd(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'StoredValueLedgers',
  data() {
    return {
      tab: 'ledger',
      loading: false,
      orderLoading: false,
      list: [],
      total: 0,
      orders: [],
      orderTotal: 0,
      summary: {
        pool_balance_fen: 0,
        recharge_fen: 0,
        consume_fen: 0,
        refund_fen: 0,
        adjust_in_fen: 0,
        adjust_out_fen: 0,
        recharge_order_pay_fen: 0,
        recharge_order_gift_fen: 0,
        recharge_order_count: 0,
        user_with_balance: 0,
      },
      query: {
        keyword: '',
        biz_type: '',
        dateRange: [],
        page: 1,
        pageSize: 20,
      },
      orderQuery: {
        page: 1,
        pageSize: 20,
        status: 'paid',
      },
      ledgerColumns: [
        {
          title: '时间',
          minWidth: 160,
          render: (h, { row }) => h('span', this.formatDate(row.created_at)),
        },
        {
          title: '用户',
          minWidth: 120,
          render: (h, { row }) =>
            h('span', row.customer_nickname || row.customer_phone || '-'),
        },
        {
          title: '手机号',
          width: 120,
          render: (h, { row }) => h('span', row.customer_phone || '-'),
        },
        {
          title: '用户ID',
          minWidth: 180,
          ellipsis: true,
          tooltip: true,
          render: (h, { row }) => h('span', row.user_id || '-'),
        },
        {
          title: '类型',
          width: 80,
          render: (h, { row }) => h('span', this.bizLabel(row.biz_type)),
        },
        {
          title: '变动',
          width: 110,
          render: (h, { row }) => {
            const fen = Number(row.delta_fen) || 0
            const text = `${fen >= 0 ? '+' : ''}¥${(fen / 100).toFixed(2)}`
            return h(
              'span',
              { style: { color: fen >= 0 ? '#19be6b' : '#ed4014', fontWeight: 600 } },
              text
            )
          },
        },
        {
          title: '变动后余额',
          width: 110,
          render: (h, { row }) =>
            h('span', `¥${((Number(row.balance_after_fen) || 0) / 100).toFixed(2)}`),
        },
        {
          title: '备注',
          minWidth: 160,
          ellipsis: true,
          tooltip: true,
          key: 'remark',
        },
        {
          title: '关联单号',
          minWidth: 160,
          ellipsis: true,
          tooltip: true,
          render: (h, { row }) => h('span', row.ref_id || '-'),
        },
      ],
      orderColumns: [
        {
          title: '支付时间',
          minWidth: 160,
          render: (h, { row }) => h('span', this.formatDate(row.paid_at || row.created_at)),
        },
        {
          title: '用户ID',
          minWidth: 180,
          ellipsis: true,
          tooltip: true,
          render: (h, { row }) => h('span', row.user_id || '-'),
        },
        {
          title: '活动',
          minWidth: 120,
          ellipsis: true,
          tooltip: true,
          render: (h, { row }) => h('span', row.activity_name || '-'),
        },
        {
          title: '实付',
          width: 100,
          render: (h, { row }) =>
            h('span', `¥${((Number(row.pay_fen) || 0) / 100).toFixed(2)}`),
        },
        {
          title: '赠送',
          width: 100,
          render: (h, { row }) =>
            h('span', `¥${((Number(row.gift_fen) || 0) / 100).toFixed(2)}`),
        },
        {
          title: '入账',
          width: 100,
          render: (h, { row }) => {
            const c =
              row.credit_fen != null
                ? Number(row.credit_fen)
                : (Number(row.pay_fen) || 0) + (Number(row.gift_fen) || 0)
            return h('span', { style: { color: '#19be6b', fontWeight: 600 } }, `¥${(c / 100).toFixed(2)}`)
          },
        },
        {
          title: '状态',
          width: 80,
          render: (h, { row }) => h('span', this.orderStatusLabel(row.status)),
        },
        {
          title: '商户单号',
          minWidth: 160,
          ellipsis: true,
          tooltip: true,
          render: (h, { row }) => h('span', row.wx_out_trade_no || '-'),
        },
      ],
    }
  },
  mounted() {
    this.load()
    this.loadOrders()
  },
  methods: {
    yuan(fen) {
      return ((Math.round(Number(fen) || 0) || 0) / 100).toFixed(2)
    },
    bizLabel(t) {
      const map = { recharge: '充值', consume: '消费', refund: '退款', adjust: '调账' }
      return map[t] || t || '-'
    },
    orderStatusLabel(s) {
      const map = { pending: '待支付', paid: '已支付', failed: '失败', closed: '关闭' }
      return map[s] || s || '-'
    },
    formatDate(v) {
      if (!v) return '-'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    buildLedgerParams() {
      const p = {
        page: this.query.page,
        pageSize: this.query.pageSize,
      }
      if (this.query.keyword) p.keyword = this.query.keyword.trim()
      if (this.query.biz_type) p.biz_type = this.query.biz_type
      const dr = this.query.dateRange
      if (Array.isArray(dr) && dr.length === 2 && dr[0] && dr[1]) {
        p.start = ymd(dr[0] instanceof Date ? dr[0] : new Date(dr[0]))
        p.end = ymd(dr[1] instanceof Date ? dr[1] : new Date(dr[1]))
      }
      return p
    },
    search() {
      this.query.page = 1
      this.orderQuery.page = 1
      this.load()
      this.loadOrders()
    },
    resetQuery() {
      this.query = {
        keyword: '',
        biz_type: '',
        dateRange: [],
        page: 1,
        pageSize: 20,
      }
      this.orderQuery = { page: 1, pageSize: 20, status: 'paid' }
      this.load()
      this.loadOrders()
    },
    onTab() {
      if (this.tab === 'orders' && !this.orders.length) this.loadOrders()
    },
    onPageChange(p) {
      if (this.tab === 'ledger') {
        this.query.page = p
        this.load()
      } else {
        this.orderQuery.page = p
        this.loadOrders()
      }
    },
    onPageSizeChange(size) {
      if (this.tab === 'ledger') {
        this.query.pageSize = size
        this.query.page = 1
        this.load()
      } else {
        this.orderQuery.pageSize = size
        this.orderQuery.page = 1
        this.loadOrders()
      }
    },
    async load() {
      this.loading = true
      try {
        const res = await storedValueLedgerReport(this.buildLedgerParams())
        if (res.data && res.data.code === 200 && res.data.data) {
          this.list = res.data.data.list || []
          this.total = res.data.data.total || 0
          if (res.data.data.summary) {
            this.summary = { ...this.summary, ...res.data.data.summary }
          }
        } else {
          this.$Message.error((res.data && res.data.msg) || '加载失败')
        }
      } catch (e) {
        this.$Message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadOrders() {
      this.orderLoading = true
      try {
        const res = await storedValueRechargeOrders({
          page: this.orderQuery.page,
          pageSize: this.orderQuery.pageSize,
          status: this.orderQuery.status || undefined,
        })
        if (res.data && res.data.code === 200 && res.data.data) {
          this.orders = res.data.data.list || []
          this.orderTotal = res.data.data.total || 0
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.orderLoading = false
      }
    },
  },
}
</script>

<style scoped>
.stat-row {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px;
}
.stat-card {
  flex: 1;
  min-width: 160px;
  margin: 8px;
  padding: 16px 18px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #eef2f7;
}
.stat-label {
  font-size: 13px;
  color: #808695;
}
.stat-value {
  margin-top: 6px;
  font-size: 22px;
  font-weight: 700;
  color: #17233d;
}
.stat-value.up {
  color: #19be6b;
}
.stat-value.down {
  color: #ed4014;
}
.stat-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #9ea7b3;
}
.m-t-8 {
  margin-top: 8px;
}
.m-t-16 {
  margin-top: 16px;
}
.m-l-10 {
  margin-left: 10px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
</style>
